/*
** Text
*/

// Using canvas instead of SVG for speed reasons
const textMeasureContext = document.createElement('canvas').getContext('2d');
// Hash table of measured strings and their width
let textMeasureDictionary = {};

export function measureText(text, font) {
  const key = `${text}|${font}`;
  // Check cache first
  if (textMeasureDictionary[key]) return textMeasureDictionary[key];

  textMeasureContext.font = font;
  // Ceil value to normalize differences in browser implementation
  const width = Math.ceil(textMeasureContext.measureText(text).width);

  // Cache width for repeat use
  textMeasureDictionary[key] = width;

  return width;
}

export function measureWidestText(strings, font) {
  let longest = 0;
  let length;

  for (let i = 0; i < strings.length; i++) {
    length = measureText(strings[i], font);
    if (length > longest) {
      longest = length;
    }
  }
  return longest;
}

export function clearTextMeasureDictionary() {
  textMeasureDictionary = {};
}

export function wrapText(text, containerWidth, font, letterSpacing = 0) {
  const adjustedWidth = containerWidth - (containerWidth * letterSpacing);
  const lines = [];

  text.split('\n').forEach((p) => {
    let line;

    p.trim().split(' ').forEach((word, index) => {
      if (index === 0) {
        line = word;
      } else if (measureText(`${line} ${word}`, font) < adjustedWidth) {
        line += ` ${word}`;
      } else {
        lines.push(line);
        line = word;
      }
    });

    lines.push(line);
  });

  return lines.join('\n');
}


/*
** Math
*/

export function floorToMultiple(value, multiplier) {
  if (multiplier === 0) {
    return 0;
  }
  return Math.floor(value / multiplier) * multiplier;
}

export function ceilToMultiple(value, multiplier) {
  if (multiplier === 0) {
    return 0;
  }
  return Math.ceil(value / multiplier) * multiplier;
}

export function orderOfMagnitude(value) {
  return Math.pow(10, Math.floor(Math.log(Math.abs(value)) / Math.LN10));
}

export function decimalPlaces(number) {
  const match = (`${number}`).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
  if (!match) return 0;

  return Math.max(
    0,
    // Number of digits right of decimal point
    (match[1] ? match[1].length : 0)
    // Adjust for scientific notation
    - (match[2] ? +match[2] : 0),
  );
}

export function numDigits(number) {
  return Math.max(Math.floor(Math.log(Math.abs(number)) * Math.LOG10E), 0) + 1;
}

export function roundTo(number, precision) {
  const mult = Math.pow(10, precision);
  return Math.round(number * mult) / mult;
}

export function roundPixels(value) {
  return roundTo(value, 2);
}

export function readableNumber(number, decimals = null, useUnits = false) {
  const digits = numDigits(number);
  let num = number;
  let precision = decimals;
  let unit = '';

  if (useUnits) {
    if (digits > 12) {
      num /= Math.pow(10, 12);
      unit = 'T';
    } else if (digits > 9) {
      num /= Math.pow(10, 9);
      unit = 'B';
    } else if (digits > 6) {
      num /= Math.pow(10, 6);
      unit = 'M';
    }

    precision = decimalPlaces(num);
  }

  const stringNumber = precision !== null ? num.toFixed(precision) : num.toString();
  const parts = stringNumber.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return `${parts.join('.')}${unit}`;
}


/*
** Positioning
*/

// Emulate event.offsetX and event.offsetY which are inconsistently implemented
export function mouseOffset(event, target = event.target) {
  const rect = target.getBoundingClientRect();
  return { x: event.clientX - rect.left, y: event.clientY - rect.top };
}

export function inBounds(x, y, left, top, right, bottom) {
  return x >= left && x <= right && y >= top && y <= bottom;
}

export function distanceBetweenPoints(x1, y1, x2, y2) {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}

export function distanceToElement(x, y, left, top, width, height) {
  const right = left + width;
  const bottom = top + height;

  let closestX;
  if (left <= x && x <= right) {
    closestX = x;
  } else {
    closestX = Math.abs(left - x) < Math.abs(right - x) ? left : right;
  }

  let closestY;
  if (top <= y && y <= bottom) {
    closestY = y;
  } else {
    closestY = Math.abs(top - y) < Math.abs(bottom - y) ? top : bottom;
  }

  return distanceBetweenPoints(x, y, closestX, closestY);
}

export function getAnchorPosition(x, y, left, top, width, height) {
  const right = left + width;
  const bottom = top + height;

  let anchorX;
  if (left <= x && x <= right) {
    anchorX = Math.round(left + (width / 2));
  } else {
    anchorX = Math.abs(left - x) < Math.abs(right - x) ? left : right;
  }

  let anchorY;
  if (top <= y && y <= bottom) {
    anchorY = Math.round(top + (height / 2));
  } else {
    anchorY = Math.abs(top - y) < Math.abs(bottom - y) ? top : bottom;
  }

  return { x: Math.round(anchorX), y: Math.round(anchorY) };
}

export function rectsOverlap(rectA, rectB, minSpacing = 0) {
  return !(
    rectA.right + minSpacing < rectB.left ||
    rectA.left > rectB.right + minSpacing ||
    rectA.bottom + minSpacing < rectB.top ||
    rectA.top > rectB.bottom + minSpacing
  );
}

export function elementsOverlap(elA, elB, minSpacing = 0) {
  return rectsOverlap(elA.getBoundingClientRect(), elB.getBoundingClientRect(), minSpacing);
}


/*
** Other
*/

export function supportsCssProperty(prop) {
  const el = document.createElement('div');
  if (prop in el.style) return true;

  const vendors = ['Khtml', 'Ms', 'O', 'Moz', 'Webkit'];
  const propCapitalized = prop.replace(/^[a-z]/, firstLetter => firstLetter.toUpperCase());

  for (let i = 0; i < vendors.length; i++) {
    if (vendors[i] + propCapitalized in el.style) {
      return true;
    }
  }

  return false;
}

export function truncateOrDefault(value, maxChars, defaultValue = null) {
  if (typeof value !== 'undefined' && value !== null) {
    return maxChars > 0 ? value.toString().slice(0, maxChars) : value.toString();
  }
  return defaultValue;
}

export function isFirstOccurrence(value, index, array) {
  return array.indexOf(value) === index;
}

export function compareX(a, b) {
  return a.x - b.x;
}
